import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/apiClient";
import { allPages } from "@/lib/pagination";
import { PageHeader, EmptyState } from "@/components/PageBits";
import { CodeBlock } from "@/components/CodeBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Mic, MicOff, Video, Monitor, UserPlus, Ban, Trash2, Key } from "lucide-react";
import { toast } from "sonner";
import RecordingControls from "@/components/RecordingControls";
import LiveRoomPanel from "@/components/LiveRoomPanel";

export default function RoomDetail() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const activeRoom = useRef(roomId);
  activeRoom.current = roomId;
  const [partsError, setPartsError] = useState("");
  const [room, setRoom] = useState(null);
  const [mediaSession, setMediaSession] = useState(null);
  const [parts, setParts] = useState([]);
  const [token, setToken] = useState(null);
  const [simOpen, setSimOpen] = useState(false);
  const [form, setForm] = useState({ identity: "", name: "", role: "participant", audio: true, video: true, screen: false });

  const loadRoom = useCallback(() => {
    api.get(`/rooms/${roomId}`).then((r) => { if (activeRoom.current === roomId) setRoom(r.data); }).catch(() => { if (activeRoom.current === roomId) setRoom(false); });
  }, [roomId]);
  const loadParts = useCallback(() => {
    allPages(`/rooms/${roomId}/participants`).then((items) => { if (activeRoom.current === roomId) { setParts(items); setPartsError(""); } }).catch((e) => { if (activeRoom.current === roomId) setPartsError(apiError(e)); });
  }, [roomId]);

  useEffect(() => {
    setRoom(null); setParts([]); setToken(null); setPartsError("");
    loadRoom(); loadParts();
    const t = setInterval(loadParts, 3000);
    return () => clearInterval(t);
  }, [loadRoom, loadParts]);

  const simulateJoin = async () => {
    if (!room?.supports_simulation || !room?.can_manage) return;
    try {
      await api.post(`/rooms/${roomId}/simulate/join`, form);
      toast.success(`${form.identity} joined`);
      setSimOpen(false);
      setForm({ identity: "", name: "", role: "participant", audio: true, video: true, screen: false });
      loadParts(); loadRoom();
    } catch (e) { toast.error(apiError(e)); }
  };

  const kick = async (identity) => {
    try { await api.post(`/rooms/${roomId}/participants/${encodeURIComponent(identity)}/kick`); toast.success(`${identity} removed`); loadParts(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const mute = async (identity) => {
    try { await api.post(`/rooms/${roomId}/participants/${encodeURIComponent(identity)}/mute`); toast.success(`${identity} muted`); loadParts(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const genToken = async () => {
    try { const { data } = await api.post(`/rooms/${roomId}/token`); setToken(data); toast.success("Token generated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this room and disconnect its participants?")) return;
    try { await api.delete(`/rooms/${roomId}`); toast.success("Room deleted"); navigate("/dashboard/rooms"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (room === false) return <EmptyState title="Room not found" />;
  if (!room) return <div className="text-sm text-zinc-500">Loading…</div>;

  return (
    <div data-testid="room-detail-page">
      <button onClick={() => navigate("/dashboard/rooms")} className="mb-4 flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-300" data-testid="back-to-rooms">
        <ArrowLeft className="h-4 w-4" /> Rooms
      </button>
      <PageHeader
        title={<span className="font-mono">{room.name}</span>}
        description={`${room.type} · provider: ${room.provider} · ${room.active_participants} live`}
        actions={
          <div className="flex gap-2">
            {room.supports_simulation && room.can_manage && <Dialog open={simOpen} onOpenChange={setSimOpen}>
              <DialogTrigger asChild>
                <Button data-testid="simulate-join-button" variant="outline" className="border-zinc-800 bg-zinc-950 hover:bg-zinc-900">
                  <UserPlus className="mr-2 h-4 w-4" /> Simulate participant
                </Button>
              </DialogTrigger>
              <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="simulate-join-dialog">
                <DialogHeader><DialogTitle className="text-zinc-100">Simulate a participant</DialogTitle></DialogHeader>
                <p className="text-xs text-zinc-500">Fake provider: this joins a simulated participant so you can see live state and usage metering without a real media server.</p>
                <div className="space-y-3 py-2">
                  <div className="space-y-2"><Label className="text-zinc-300">Identity</Label>
                    <Input data-testid="sim-identity" value={form.identity} onChange={(e) => setForm({ ...form, identity: e.target.value })} className="bg-zinc-950 border-zinc-800 font-mono" placeholder="alice" /></div>
                  <div className="space-y-2"><Label className="text-zinc-300">Display name</Label>
                    <Input data-testid="sim-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800" placeholder="Alice" /></div>
                  <div className="space-y-2"><Label className="text-zinc-300">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="sim-role" className="bg-zinc-950 border-zinc-800"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-zinc-800 bg-zinc-950">
                        <SelectItem value="host">Host</SelectItem>
                        <SelectItem value="cohost">Co-host</SelectItem>
                        <SelectItem value="participant">Participant</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-6 pt-1">
                    {["audio", "video", "screen"].map((k) => (
                      <label key={k} className="flex items-center gap-2 text-sm text-zinc-300">
                        <Switch data-testid={`sim-${k}`} checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} /> {k}
                      </label>
                    ))}
                  </div>
                </div>
                <DialogFooter>
                  <Button data-testid="sim-join-confirm" disabled={!form.identity} onClick={simulateJoin} className="bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Join</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>}
            <Button disabled={!room.can_manage} data-testid="delete-room-button" variant="outline" onClick={remove} className="border-red-900/50 bg-zinc-950 text-red-400 hover:bg-red-950/40">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      {room.can_connect && <LiveRoomPanel key={roomId} roomId={roomId} audioOnly={room.type === "audio"} onSession={setMediaSession} />}
      {room.can_connect && <RecordingControls key={`recording-${roomId}`} kind="rooms" resourceId={roomId} mediaSession={mediaSession} />}
      {!room.can_connect && !room.supports_simulation && <p role="status" className="mb-4 text-sm text-amber-400">Cette salle appartient à un ancien fournisseur. Créez une nouvelle salle avec le fournisseur actuel.</p>}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="rounded-md border border-zinc-800 bg-zinc-900/30">
            <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
              <h2 className="text-sm font-medium text-zinc-200">Participants</h2>
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
                <span className={`h-1.5 w-1.5 rounded-full ${parts.length ? "bg-emerald-500 animate-pulse" : "bg-zinc-600"}`} />
                live · refreshes every 3s
              </span>
            </div>
            {partsError && <p role="alert" className="p-4 text-sm text-red-400">{partsError}</p>}
            {parts.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-zinc-500" data-testid="no-participants">No one is in this room. Join the room or connect your application with a participant token.</div>
            ) : (
              <ul className="divide-y divide-zinc-800" data-testid="participants-list">
                {parts.map((p) => (
                  <li key={p.identity} className="flex items-center justify-between px-5 py-3" data-testid={`participant-${p.identity}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-800 text-xs font-medium text-zinc-200">{(p.name || p.identity)[0]?.toUpperCase()}</div>
                      <div>
                        <div className="text-sm text-zinc-100">{p.name || p.identity}</div>
                        <div className="font-mono text-xs text-zinc-500">{p.identity} · {p.role}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded p-1 ${p.audio ? "text-emerald-400" : "text-zinc-600"}`}>{p.audio ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}</span>
                      <span className={`rounded p-1 ${p.video ? "text-emerald-400" : "text-zinc-600"}`}><Video className="h-4 w-4" /></span>
                      <span className={`rounded p-1 ${p.screen ? "text-emerald-400" : "text-zinc-600"}`}><Monitor className="h-4 w-4" /></span>
                      <button disabled={!room.can_manage} aria-label={`Mute ${p.identity}`} data-testid={`mute-${p.identity}`} onClick={() => mute(p.identity)} className="ml-2 rounded border border-zinc-800 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800"><MicOff className="h-3.5 w-3.5" /></button>
                      <button disabled={!room.can_manage} aria-label={`Remove ${p.identity}`} data-testid={`kick-${p.identity}`} onClick={() => kick(p.identity)} className="rounded border border-red-900/50 px-2 py-1 text-xs text-red-400 hover:bg-red-950/40"><Ban className="h-3.5 w-3.5" /></button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-200"><Key className="h-4 w-4" /> Token playground</div>
            <p className="mt-2 text-xs text-zinc-500">Generate a short-lived token for your account with permissions matching your organization role.</p>
            <Button data-testid="generate-token-button" onClick={genToken} className="mt-4 w-full bg-zinc-50 text-zinc-900 hover:bg-zinc-200">Generate token</Button>
            {token && (
              <div className="mt-4 space-y-2" data-testid="token-result">
                <CodeBlock testId="token-code" language="wsUrl" code={token.ws_url} />
                <CodeBlock testId="token-jwt" language="access token" code={token.token} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
